import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  File,
  Loader2,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

function fileKey(file) {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

export default function UploadContractUploadStep({
  dismissJob,
  dragActive,
  error,
  files,
  handleDrag,
  handleDrop,
  handleFilesSelect,
  handleUpload,
  jobs,
  removeStagedFile,
  searchParams,
  uploadCount,
  uploading,
}) {
  // needs_review contracts are only visible with write_contracts, so for an
  // upload-only grantee the created contract can't be opened (404-as-absent)
  // until legal reviews it — the job card says so instead of linking.
  const { canWriteContracts } = useAuth();
  // Dropzone styling — accent the border in the active / files-loaded
  // states, leave the background neutral. The previous blue/green
  // surface tints dragged the body text below contrast in dark mode.
  const dropBorder = dragActive
    ? 'var(--surface-blue-strong, #0072db)'
    : files.length
      ? 'var(--surface-green-strong, #11a967)'
      : 'var(--border-primary)';
  const dropBg = 'var(--surface-panel)';

  const stagedTotalMb = files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024;
  const uploadButtonLabel = uploading
    ? (uploadCount.total > 1
        ? `Uploading ${uploadCount.done} of ${uploadCount.total}…`
        : 'Uploading…')
    : (files.length > 1 ? `Upload ${files.length} files` : 'Upload');

  return (
    <div className="canvas-inner animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div style={{ minWidth: 0 }}>
          <div className="page-eyebrow" style={{ marginBottom: 8 }}>
            <Link
              to="/contracts"
              className="row"
              style={{ gap: 4, color: 'inherit', textDecoration: 'none' }}
            >
              <ArrowLeft className="w-3 h-3" />
              Contracts
            </Link>
          </div>
          <h1 className="page-title">Upload contract</h1>
          <div className="page-subtitle">
            Upload one or more PDFs or Word documents — Docket extracts contract details for each one in parallel.
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="callout red" style={{ marginBottom: 20 }}>
          <AlertCircle className="w-4 h-4" style={{ color: 'var(--text-critical)', flexShrink: 0, marginTop: 1 }} />
          <div className="callout-body">
            <strong>Upload error</strong>
            <div style={{ fontSize: 12, marginTop: 2 }}>{error}</div>
          </div>
        </div>
      )}

      {/* Upload card */}
      <div className="card">
        <div className="card-body" style={{ padding: 20 }}>
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            style={{
              position: 'relative',
              border: `1px dashed ${dropBorder}`,
              borderRadius: 10,
              padding: files.length ? '24px' : '48px 24px',
              textAlign: 'center',
              background: dropBg,
              transition: 'background 150ms, border-color 150ms',
            }}
          >
            {files.length === 0 ? (
              <>
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 999,
                    background: 'var(--surface-base)',
                    border: 'var(--stroke-xs) solid var(--border-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 14px',
                  }}
                >
                  <Upload className="w-5 h-5" style={{ color: 'var(--icon-tertiary)' }} />
                </div>
                <label style={{ cursor: 'pointer', fontSize: 14, color: 'var(--text-primary)' }}>
                  <span style={{ fontWeight: 500, color: 'var(--text-blue)' }}>Click to upload</span>
                  <span style={{ color: 'var(--text-secondary)' }}> or drag and drop one or more files</span>
                  <input
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    accept=".pdf,.docx,.doc"
                    onChange={(e) => {
                      if (e.target.files?.length) handleFilesSelect(e.target.files);
                      // Reset so re-selecting the same file fires onChange again.
                      e.target.value = '';
                    }}
                  />
                </label>
                <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 6 }}>
                  PDF, DOCX, or DOC up to 50MB each
                </p>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    marginBottom: 2,
                  }}
                >
                  <span>
                    {files.length} file{files.length === 1 ? '' : 's'} staged
                    <span style={{ color: 'var(--text-tertiary)', marginLeft: 6 }}>
                      ({stagedTotalMb.toFixed(2)} MB total)
                    </span>
                  </span>
                  <label style={{ cursor: 'pointer', color: 'var(--text-blue)', fontWeight: 500 }}>
                    + Add more
                    <input
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      accept=".pdf,.docx,.doc"
                      onChange={(e) => {
                        if (e.target.files?.length) handleFilesSelect(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                {files.map((file) => (
                  <div
                    key={fileKey(file)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 10px',
                      background: 'var(--surface-base)',
                      border: 'var(--stroke-xs) solid var(--border-primary)',
                      borderRadius: 8,
                      textAlign: 'left',
                    }}
                  >
                    <File className="w-4 h-4" style={{ color: 'var(--text-green)', flexShrink: 0 }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 13,
                          color: 'var(--text-primary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.name}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeStagedFile(fileKey(file))}
                      className="icon-btn xs"
                      title="Remove from upload"
                      aria-label="Remove from upload"
                      disabled={uploading}
                    >
                      <X className="w-[14px] h-[14px]" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upload progress — visible while the queue is flushing. */}
          {uploading && uploadCount.total > 0 && (
            <div style={{ marginTop: 18 }} className="animate-fade-in">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  fontSize: 12,
                  marginBottom: 6,
                  color: 'var(--text-secondary)',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Uploading {uploadCount.done} of {uploadCount.total}…
                </span>
                <span style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round((uploadCount.done / uploadCount.total) * 100)}%
                </span>
              </div>
              <div
                style={{
                  width: '100%',
                  height: 4,
                  background: 'var(--surface-panel)',
                  border: 'var(--stroke-xs) solid var(--border-primary)',
                  borderRadius: 999,
                  overflow: 'hidden',
                }}
              >
                <div
                  className="fc-meter"
                  style={{
                    width: `${Math.round((uploadCount.done / uploadCount.total) * 100)}%`,
                    height: '100%',
                    background: 'var(--text-blue)',
                    transition: 'width 300ms',
                  }}
                />
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  marginTop: 6,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--text-blue)' }} />
                Each file kicks off an AI analysis once uploaded.
              </p>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
            <button
              onClick={handleUpload}
              disabled={!files.length || uploading}
              className="btn primary"
            >
              {uploading ? <Loader2 className="animate-spin" /> : <Upload />}
              {uploadButtonLabel}
            </button>
          </div>
        </div>
      </div>

      {/* Recent extractions — running + recently-completed jobs. Hidden
          when empty so a first-time user doesn't see an empty shell. */}
      {jobs.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-header compact">
            <span className="card-title">Recent extractions</span>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
              Contracts are saved automatically — cards clear 30 min after completion
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {jobs.map((job) => {
              const highlighted = searchParams.get('job') === job.jobId;
              const title =
                job.contractTitle ||
                (job.extracted && job.extracted.title) ||
                job.tempFileName ||
                'Extraction';
              return (
                <div
                  key={job.jobId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 20px',
                    borderTop: 'var(--stroke-xs) solid var(--border-primary)',
                    background: highlighted ? 'var(--surface-hover)' : 'transparent',
                  }}
                >
                  {/* Status icon */}
                  <div style={{ flexShrink: 0, width: 20, display: 'flex', justifyContent: 'center' }}>
                    {job.status === 'running' && (
                      <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--text-blue)' }} />
                    )}
                    {job.status === 'done' && (
                      <CheckCircle2 className="w-4 h-4" style={{ color: 'var(--text-green)' }} />
                    )}
                    {job.status === 'error' && (
                      <AlertCircle className="w-4 h-4" style={{ color: 'var(--text-critical)' }} />
                    )}
                  </div>

                  {/* Title + status line */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'var(--text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {title}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {job.status === 'running' && 'AI is analyzing…'}
                      {job.status === 'done' && (
                        <>
                          {canWriteContracts ? 'Created — needs review' : 'Uploaded — awaiting legal review'}
                          {job.confidence ? ` · confidence: ${job.confidence}` : ''}
                          {job.possibleDuplicateOf ? ' · possible duplicate' : ''}
                        </>
                      )}
                      {job.status === 'error' && (job.error || 'Extraction failed')}
                    </div>
                  </div>

                  {/* Action */}
                  <div style={{ flexShrink: 0, display: 'flex', gap: 6 }}>
                    {job.status === 'done' && job.contractId && canWriteContracts && (
                      <Link
                        to={`/contracts/${job.contractId}`}
                        className="btn primary sm"
                      >
                        View contract
                      </Link>
                    )}
                    {job.status !== 'running' && (
                      <button
                        type="button"
                        onClick={() => dismissJob(job.jobId)}
                        className="icon-btn xs"
                        title="Dismiss"
                        aria-label="Dismiss"
                      >
                        <X className="w-[14px] h-[14px]" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}
